import { PortalProvider } from "@gorhom/portal";
import { useFonts } from "expo-font";
import React, { FC, useMemo } from "react";
import { Platform } from "react-native";
import { SafeAreaProvider } from "react-native-safe-area-context";
import { Aurora } from "@lookiero/aurora";
import { Locale } from "@lookiero/sty-psp-locale";
import { SentryEnvironment } from "@lookiero/sty-psp-logging/sentry";
import { themeByTradename } from "@lookiero/sty-psp-ui";
import { Tradename } from "@lookiero/sty-sp-tradename";
import { bootstrap as lookAndLikeBootstrap } from "./infrastructure/delivery/bootstrap";
import { root } from "./infrastructure/ui/Root";
import { RELEASE } from "./version";

const tradename = Tradename.LOOKIERO;
const theme = themeByTradename({ tradename });
const locale: Locale = Locale.es_ES;

const sentryConfig: SentryEnvironment = {
  publicKey: "a6beababbfccbf57ffdd7408aa9a2b2a",
  release: RELEASE,
  project: "4507622227574784",
  environment: "development",
  dist: "web-standalone",
};

const apiUrl =
  Platform.OS !== "web"
    ? "https://web2.sp.dev.aws.lookiero.es/look-and-like/api"
    : __DEV__
      ? process.env.EXPO_PUBLIC_CI
        ? "/local-to-dev"
        : "http://localhost:3000/local-to-dev"
      : "/look-and-like/api";

interface ExpoRootProps {
  readonly customerId: string;
  readonly getAuthToken: () => Promise<string>;
}

const ExpoRoot: FC<ExpoRootProps> = ({ customerId, getAuthToken }) => {
  const [fontsLoaded] = useFonts({
    ["AreaInktrap-Semibold"]: require("@lookiero/aurora/build/assets/fonts/AreaInktrap-Semibold.otf"),
    ["AreaNormal-Semibold"]: require("@lookiero/aurora/build/assets/fonts/AreaNormal-Semibold.otf"),
    ["AreaNormal-Extrabold"]: require("@lookiero/aurora/build/assets/fonts/AreaNormal-Extrabold.otf"),
    ["OpenSans-Regular"]: require("@lookiero/aurora/build/assets/fonts/OpenSans-Regular.ttf"),
    ["OpenSans-Bold"]: require("@lookiero/aurora/build/assets/fonts/OpenSans-Bold.ttf"),
    auroraicons: require("@lookiero/aurora-iconfont/dist/auroraicons.ttf"),
  });

  const Root = useMemo(() => {
    const { Component: Messaging } = lookAndLikeBootstrap({ apiUrl: () => apiUrl, getAuthToken });

    return root({
      Messaging,
      development: false,
      sentry: () => sentryConfig,
    })({
      customerId: undefined,
    });
  }, [getAuthToken]);

  return fontsLoaded ? (
    <SafeAreaProvider style={{ overflow: "hidden" }}>
      <Aurora theme={theme}>
        <PortalProvider>
          <Root customerId={customerId} locale={locale} tradename={tradename} view="look-and-like" />
        </PortalProvider>
      </Aurora>
    </SafeAreaProvider>
  ) : null;
};

export { ExpoRoot };
